<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDeviceTypes } from '@/composables/equipment/useEquipmentLookups';
import type { IDeviceGroup } from '@/types/equipment.types';
import EquipmentTypeIcon from './EquipmentTypeIcon.vue';

const props = defineProps<{ group: IDeviceGroup; editable: boolean }>();
defineEmits<{ edit: []; remove: [] }>();

const { t } = useI18n({ useScope: 'global' });
const { typeLabel } = useDeviceTypes();

// Le libellé du maître contient parfois déjà son type : on ne le répète pas.
const masterLine = computed(() => {
  const { label, type } = props.group.master;
  const kind = typeLabel(type);
  return label.includes(kind) ? label : `${label} · ${kind}`;
});

// Un bloc par type d'équipement, le par défaut d'abord puis ses replis dans l'ordre.
const sections = computed(() => {
  const byType = new Map<string, IDeviceGroup['members']>();
  for (const member of props.group.members) {
    byType.set(member.type, [...(byType.get(member.type) ?? []), member]);
  }
  return [...byType].map(([type, members]) => {
    const sorted = [...members].sort(
      (a, b) =>
        Number(b.placement === 'default') - Number(a.placement === 'default') ||
        (a.fallbackIndex ?? 0) - (b.fallbackIndex ?? 0),
    );
    return {
      type,
      members: sorted,
      fallbacks: sorted.filter((m) => m.placement !== 'default').length,
    };
  });
});
</script>

<template>
  <article class="grc eq-card">
    <header class="grc__head">
      <span class="eq-chip"
        ><EquipmentTypeIcon :type="group.master.type"
      /></span>
      <div class="grc__titles">
        <h3 class="grc__name">{{ group.name }}</h3>
        <p class="grc__sub">{{ masterLine }}</p>
      </div>
      <div v-if="editable" class="eq-actions">
        <button
          type="button"
          class="eq-btn eq-btn--icon"
          :aria-label="t('equipment.edit')"
          @click="$emit('edit')"
        >
          <i class="pi pi-pencil" />
        </button>
        <button
          type="button"
          class="eq-btn eq-btn--icon"
          :aria-label="t('equipment.delete')"
          @click="$emit('remove')"
        >
          <i class="pi pi-trash" />
        </button>
      </div>
    </header>

    <section v-for="section in sections" :key="section.type" class="grc__sec">
      <h4 class="grc__sectitle">
        <EquipmentTypeIcon :type="section.type" />
        {{ typeLabel(section.type) }}
        <span v-if="section.fallbacks" class="grc__count">
          · {{ t('equipment.groups.fallbackCount', { n: section.fallbacks }) }}
        </span>
      </h4>
      <ul class="grc__members">
        <li
          v-for="member in section.members"
          :key="member.id"
          class="grc__member"
          :class="{ 'grc__member--fallback': member.placement !== 'default' }"
        >
          <EquipmentTypeIcon :type="member.type" />
          <span class="eq-stack">
            <strong>{{ member.label }}</strong>
          </span>
          <span
            class="eq-badge grc__badge"
            :class="
              member.placement === 'default'
                ? 'grc__badge--default'
                : 'grc__badge--fallback'
            "
          >
            {{
              member.placement === 'default'
                ? t('equipment.groups.default')
                : t('equipment.groups.fallback', {
                    n: member.fallbackIndex ?? 1,
                  })
            }}
          </span>
        </li>
      </ul>
    </section>
    <p v-if="!group.members.length" class="eq-muted">
      {{ t('equipment.groups.noMembers') }}
    </p>
  </article>
</template>

<style scoped>
.grc {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  padding: 1.1rem 1.25rem;
}
.grc__head {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.grc__titles {
  flex: 1;
  min-width: 0;
}
.grc__name {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.grc__sub {
  margin: 0.15rem 0 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
}
.grc__badge--default {
  background: #dcfce7;
  color: #166534;
}
.grc__badge--fallback {
  background: #fef3c7;
  color: #92400e;
}
.grc__sec {
  padding-top: 0.75rem;
  border-top: 1px dashed var(--nxt-surface-high);
}
.grc__sectitle {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0 0 0.35rem;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nxt-on-surface-variant);
}
.grc__count {
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
}
.grc__members {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
}
.grc__member {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.55rem 0;
  font-size: 0.9375rem;
  color: var(--nxt-on-surface);
}
.grc__member--fallback {
  position: relative;
  margin-left: 0.6rem;
  padding: 0.35rem 0 0.35rem 1.9rem;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
}
.grc__member--fallback::before {
  content: '';
  position: absolute;
  top: -0.55rem;
  bottom: 50%;
  left: 0.55rem;
  width: 0.9rem;
  border-left: 2px solid var(--nxt-outline);
  border-bottom: 2px solid var(--nxt-outline);
  border-bottom-left-radius: 0.6rem;
}
.grc__member .eq-stack {
  flex: 1;
  min-width: 0;
}
</style>
